import { type CSSProperties, type ReactNode } from "react";
import styled from "@emotion/styled";
import { IconButton } from "../../actions/IconButton";

export type WorkspaceHeaderProps = {
  /** Brand block (logo + product name) rendered at the far left. */
  brand?: ReactNode;
  /** Fixed width (px) for the brand rail, e.g. to align with a sidebar. Defaults to 216. */
  brandWidth?: number;
  /** Collapses the brand rail to the sidebar-toggle column and hides the brand slot. */
  brandCollapsed?: boolean;
  /** Fixed width (px) for the collapsed brand rail. Defaults to 56. */
  collapsedBrandWidth?: number;
  /** When provided, renders a sidebar-toggle button next to the brand. */
  onToggleSidebar?: () => void;
  /** Accessible label for the sidebar-toggle button. */
  toggleLabel?: string;
  /** Optional tab strip, rendered as its own bordered column after the brand. */
  tabs?: ReactNode;
  /** Fixed width (px) for the tabs column, e.g. to align with a secondary pane. Defaults to 380. */
  tabsWidth?: number;
  /** Collapses the tabs rail to a compact centered column. */
  tabsCollapsed?: boolean;
  /** Fixed width (px) for the collapsed tabs rail. Defaults to 40. */
  collapsedTabsWidth?: number;
  /** Search control, placed at the start of the trailing actions area. */
  search?: ReactNode;
  /** Fixed width (px) for the search slot wrapper. Defaults to 300. */
  searchWidth?: number;
  /** Trailing action controls (wallet, apps, avatar, …). */
  actions?: ReactNode;
  /** Free-form content placed in the main (flex) region before the spacer. */
  children?: ReactNode;
  /** Header height in px. Defaults to 52. */
  height?: number;
  className?: string;
  style?: CSSProperties;
};

/* Exact compiled output of the pre-migration Tailwind utilities. The
 * pre-existing dead class `bfrs-[scrollbar-width:none]` is intentionally not
 * reproduced. The toggle-button override uses `&&` so it beats the Button's
 * own size/colour rules, mirroring the old tailwind-merge result. */

const WorkspaceHeaderRoot = styled("header", {
  label: "AgenticWorkspaceHeader-root"
})({
  position: "relative",
  zIndex: 20,
  display: "flex",
  flexShrink: 0,
  alignItems: "stretch",
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const railBase = {
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: ".5rem",
  borderRightWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-strong) / var(--tw-border-opacity, 1))",
  transitionProperty: "width,min-width",
  transitionTimingFunction: "cubic-bezier(0,0,.2,1)",
  transitionDuration: ".22s"
} as const;

const WorkspaceHeaderBrandRail = styled("div", {
  label: "AgenticWorkspaceHeader-brandRail",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { collapsed: boolean } }>(({ ownerState }) => [
  railBase,
  ownerState.collapsed
    ? { justifyContent: "center", paddingLeft: 0, paddingRight: 0 }
    : { justifyContent: "flex-start", paddingLeft: "1rem", paddingRight: "11px" }
]);

const WorkspaceHeaderToggle = styled(IconButton, {
  label: "AgenticWorkspaceHeader-toggle",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { pushRight: boolean } }>(({ ownerState }) => [
  {
    "&&": {
      width: "26px",
      height: "26px",
      borderRadius: "6px",
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
      "&:hover": {
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
      }
    }
  },
  ownerState.pushRight && { "&&": { marginLeft: "auto" } }
]);

const WorkspaceHeaderTabsRail = styled("div", {
  label: "AgenticWorkspaceHeader-tabsRail",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { collapsed: boolean } }>(({ ownerState }) => [
  railBase,
  {
    overflowX: "hidden",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
  },
  ownerState.collapsed
    ? { justifyContent: "center", paddingLeft: 0, paddingRight: 0 }
    : { justifyContent: "flex-start", paddingLeft: ".875rem", paddingRight: ".5rem" }
]);

const WorkspaceHeaderMain = styled("div", {
  label: "AgenticWorkspaceHeader-main"
})({
  display: "flex",
  flex: "1 1 0%",
  alignItems: "center",
  gap: ".5rem",
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: "1.25rem",
  paddingRight: ".75rem"
});

const WorkspaceHeaderSearch = styled("div", {
  label: "AgenticWorkspaceHeader-search"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center"
});

const WorkspaceHeaderSpacer = styled("div", {
  label: "AgenticWorkspaceHeader-spacer"
})({
  flex: "1 1 0%"
});

/**
 * Application top bar for workspace-style layouts. Slot-based: drop in a brand
 * block, an optional sidebar toggle, a tab strip, a search control, and trailing
 * actions. All product-specific logic (which tabs exist, wallet balance, etc.)
 * stays in the consuming app — this component only owns layout and chrome.
 */
export function WorkspaceHeader({
  brand,
  brandWidth = 216,
  brandCollapsed = false,
  collapsedBrandWidth = 56,
  onToggleSidebar,
  toggleLabel = "Toggle sidebar",
  tabs,
  tabsWidth = 380,
  tabsCollapsed = false,
  collapsedTabsWidth = 40,
  search,
  searchWidth = 280,
  actions,
  children,
  height = 52,
  className,
  style
}: WorkspaceHeaderProps) {
  const resolvedBrandWidth = brandCollapsed ? collapsedBrandWidth : brandWidth;
  const resolvedTabsWidth = tabsCollapsed ? collapsedTabsWidth : tabsWidth;

  return (
    <WorkspaceHeaderRoot className={className} style={{ height, ...style }}>
      {(brand || onToggleSidebar) && (
        <WorkspaceHeaderBrandRail
          ownerState={{ collapsed: brandCollapsed }}
          style={{ width: resolvedBrandWidth, minWidth: resolvedBrandWidth }}
        >
          {!brandCollapsed && brand}
          {onToggleSidebar && (
            <WorkspaceHeaderToggle
              aria-label={toggleLabel}
              title={toggleLabel}
              variant="secondary"
              size="sm"
              ownerState={{ pushRight: !brandCollapsed }}
              icon={
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                  <rect x="1" y="1" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M5 1v11" stroke="currentColor" strokeWidth="1.3" />
                </svg>
              }
              onClick={onToggleSidebar}
            />
          )}
        </WorkspaceHeaderBrandRail>
      )}

      {tabs && (
        <WorkspaceHeaderTabsRail
          ownerState={{ collapsed: tabsCollapsed }}
          style={{ width: resolvedTabsWidth, minWidth: resolvedTabsWidth }}
        >
          {tabs}
        </WorkspaceHeaderTabsRail>
      )}

      <WorkspaceHeaderMain>
        {children}
        {search && <WorkspaceHeaderSearch style={{ width: searchWidth }}>{search}</WorkspaceHeaderSearch>}
        <WorkspaceHeaderSpacer />
        {actions}
      </WorkspaceHeaderMain>
    </WorkspaceHeaderRoot>
  );
}

WorkspaceHeader.displayName = "WorkspaceHeader";
